import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { StressResultsService } from '../../services/stress-results.service';
import { lastValueFrom } from 'rxjs';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Store } from '@ngneat/elf';
import { LoadPressureResultsUi } from '../../models/perivis-results-state.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { DisplacementResultsModel } from '../../models/displacement-results.model';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';

@Component({
  selector: 'app-pressure-results',
  templateUrl: './pressure-results.component.html',
  styles: [],
  standalone: false
})
export class PressureResultsComponent implements OnInit, OnDestroy {
  private pressureUnit: string;
  private tempUnit: string;
  private selectedString: string;

  public displacementResults: DisplacementResultsModel[];
  public yAxisTitle = '';
  public xAxisTitle = '';
  public plotName: string;
  public isLoading = signal<boolean>(true);
  public errorMsg: string;
  public results: any[];
  public columnDefs: { header: string, field: string }[];
  public columnDefinitions: any[];
  public selectedLoadCase: any;
  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];
  public resultsView: SelectItem[] = [
    { label: 'IPP', value: 'internalPressure' },
    { label: 'EPP', value: 'externalPressure' },
    { label: 'DIFF', value: 'differentialPressure' },
    { label: 'TEMP', value: 'temperature' }
  ];
  public depthView = [
    { label: 'TVD', value: 'tvd' },
    { label: 'MD', value: 'md' }
  ];
  public depthUnit: string;

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.pressureResultsStore = this._storeService.createStore(this.componentId, new LoadPressureResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedResultsView = this.pressureResultsStore.state.selectedResultsView || 'internalPressure';
    this.selectedResultDisplay = this.pressureResultsStore.state.selectedResultDisplay || 'plot';
    this.selectedDepthView = this.pressureResultsStore.state.selectedDepthView || 'tvd';
  }
  get componentId(): string {
    return this._componentId;
  }
  public pressureResultsStore: Store;

  public selectedResultsView: string = 'internalPressure';
  public selectedResultDisplay: string = 'plot';
  public selectedDepthView: string = 'tvd';

  constructor(
    private _stressResultsService: StressResultsService,
    private _signalRService: SignalRService,
    private _storeService: StoreService
  ) { }

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    const units = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.depthUnit = units.longLengths;
    this.pressureUnit = unitsLib[units.pressure].symbol;
    this.tempUnit = unitsLib[units.temperature].symbol;

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    this.refreshResults(false);
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS) {
      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      this.refreshResults(true);
    }
  }

  setPlotName(): string {
    return 'pressureResultsPlot - ' + this.selectedString + ' - ' + this.selectedResultsView + ' - ' + this.selectedDepthView;
  }

  resultsViewToggle(e) {
    this.selectedResultsView = e.value;
    this.pressureResultsStore.update(state => ({ ...state, selectedResultsView: e.value }));
    this.plotName = this.setPlotName();
    this.refreshResults(false);
  }

  onSelectedResultDisplayUpdated(e) {
    if (e.value === 'plot' || e.value === 'grid') {
      this.selectedResultDisplay = e.value;
    }
    this.pressureResultsStore.update(state => ({ ...state, selectedResultDisplay: e.value }));
  }

  onSelectedDepthViewUpdated(e) {
    this.selectedDepthView = e.value;
    this.pressureResultsStore.update(state => ({ ...state, selectedDepthView: e.value }));
    this.plotName = this.setPlotName();
  }

  public async refreshResults(calledFromSignalR: boolean): Promise<void> {
    this.isLoading.set(true);
    this.results = [];

    if (!this.displacementResults || calledFromSignalR) {
      try {
        this.displacementResults = await lastValueFrom(this._stressResultsService.getDisplacementResults());
      } catch {
        this.errorMsg = "Well Configuration Missing: Please specify at least one string";
        this.isLoading.set(false);
        return;
      }
    }

    if (this.displacementResults.length === 0 || this.displacementResults[0].loadCaseName === '') {
      this.errorMsg = "Please calculate PERIVIS to see results";
      this.columnDefs = [];
      this.results = [];
      this.isLoading.set(false);
      return;
    } else {
      this.errorMsg = null;
    }

    this.handlePressureResults(this.displacementResults);
    this.plotName = this.setPlotName();
    this.isLoading.set(false);
  }

  private handlePressureResults(displacementResults: DisplacementResultsModel[]) {
    this.columnDefinitions = [
      {
        header: `MD (${this.depthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.depthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      }
    ];
    this.columnDefs = [];
    this.results = [];

    this.yAxisTitle = `MD (${this.depthUnit})`;

    switch (this.selectedResultsView) {
      case 'internalPressure':
        this.xAxisTitle = 'Internal Pressure (' + this.pressureUnit + ')';
        break;
      case 'externalPressure':
        this.xAxisTitle = 'External Pressure (' + this.pressureUnit + ')';
        break;
      case 'differentialPressure':
        this.xAxisTitle = 'Differential Pressure (' + this.pressureUnit + ')';
        break;
      case 'temperature':
        this.xAxisTitle = 'Temperature (' + this.tempUnit + ')';
        break;
    }

    displacementResults.forEach(load => {
      if (load == null) {
        return;
      }
      const loadName: string = load.loadCaseName + ' (' + (this.selectedResultsView == 'temperature' ? this.tempUnit : this.pressureUnit) + ')';
      const loadNameCamel = toCamelCaseString(load.loadCaseName);

      this.columnDefs.push({ header: load.loadCaseName, field: loadNameCamel });

      this.columnDefinitions.push({
        header: loadName,
        field: loadNameCamel,
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      });

      const backupArray = [];

      load.displacementResults.forEach(loadResult => {

        const result = {
          [loadNameCamel]: loadResult[this.selectedResultsView],
          measuredDepth: loadResult.measuredDepth,
          trueVerticalDepth: loadResult.trueVerticalDepth
        }

        if (displacementResults[0].loadCaseName === load.loadCaseName) {
          this.results.push(result);
        } else {
          backupArray.push(result);
        }
      });

      this.results = this.results.map(x => {
        const otherLoad = backupArray.find(e => e.measuredDepth === x.measuredDepth)
        return { ...x, ...otherLoad }
      });
    });
  }

  private formatDecimal(value: any, divisor: number): string {
    return value || value === 0 ? (Math.trunc(value * divisor) / divisor).toLocaleString('en-US') : null;
  }

  ngOnDestroy(): void {
    this.signalRfunc = null;
  }
}
